import {
    orderEventIdFactory,
    orderEventIdZodSchema,
} from "@@/shared/orders/order-events/events/_order-event-id";
import { priceZodSchema } from "@@/shared/prices/price";
import { systemUserZodSchema } from "@@/shared/users/system-user";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { z } from "zod";
import { signatureFactory } from "../../signature-factory";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";

const orderEventPaymentMethodSwitchedZodObject = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("PAYMENT_METHOD_SWITCHED"),
        switchedBy: userIdZodSchema.or(systemUserZodSchema),
        switchedFrom: z.enum([
            "SWITCH_FROM_PAY_REQUEST",
            "SWITCH_FROM_ON_PREMISE_PAY_LATER",
            "UNKNOWN",
        ]),
        switchedTo: z.enum([
            "SWITCH_TO_PAY_REQUEST",
            "SWITCH_TO_ON_PREMISE_PAID",
            "SWITCH_TO_ON_PREMISE_PAY_LATER",
            "SWITCH_TO_WAIVED",
        ]),
        switchedAmount: priceZodSchema,
        reason: z.string().optional(),
    }),
);

type OrderPaymentMethodSwitchedEvent = z.infer<
    typeof orderEventPaymentMethodSwitchedZodObject
>;

const _orderEventPaymentMethodSwitchedFactoryParams =
    orderEventPaymentMethodSwitchedZodObject
        .omit({
            _id: true,
            _type: true,
            _createdAt: true,
            type: true,
            signature: true,
        })
        .and(z.object({ _id: orderEventIdZodSchema.optional() }));
type FactoryParams = z.infer<
    typeof _orderEventPaymentMethodSwitchedFactoryParams
>;

function orderEventPaymentMethodSwitchedFactory(
    params: FactoryParams,
): OrderPaymentMethodSwitchedEvent {
    const { switchedFrom, switchedTo, switchedBy, switchedAmount } = params;
    return {
        ..._orderEventBaseFactory({
            ...params,
            signature: signatureFactory(
                params.orderId,
                "PAYMENT_METHOD_SWITCHED",
                [params._version.toString()],
            ),
        }),
        switchedFrom,
        switchedTo,
        switchedBy,
        switchedAmount,
        type: "PAYMENT_METHOD_SWITCHED",
        _id: params._id ?? orderEventIdFactory(),
    };
}

const isOrderPaymentMethodSwitchedEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderPaymentMethodSwitchedEvent => {
    return (
        event._type === "ORDER_EVENT" &&
        event?.type === "PAYMENT_METHOD_SWITCHED"
    );
};

export {
    isOrderPaymentMethodSwitchedEvent,
    orderEventPaymentMethodSwitchedFactory,
    orderEventPaymentMethodSwitchedZodObject,
};
export type { OrderPaymentMethodSwitchedEvent };
